import { useState, useEffect } from 'react'
import { X, ChevronDown } from 'lucide-react'
import { Button } from '@trafiflow/shared-ui'
import type { FunnelStep, CreateStepPayload } from '../../types'
import { OfferSetEditor } from './OfferSetEditor'
import { OfferEditor, type OfferEditorValue } from './OfferEditor'
import { DialogPortal } from '../shared/DialogPortal'
import { inputClass, selectClass, labelClass } from '../../styles/class-utils'

interface EditStepModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: CreateStepPayload, offerDraft?: OfferEditorValue) => Promise<void> | void
  initialData?: Partial<FunnelStep>
  stepsCount: number
  allSteps?: FunnelStep[]
}

const STEP_TYPE_OPTIONS = [
  { value: 'upsell', label: 'Upsell' },
  { value: 'downsell', label: 'Downsell' },
  { value: 'checkout', label: 'Checkout' },
]

const EXPERIENCE_TYPE_OPTIONS = [
  { value: 'upsell', label: 'Upsell Experience' },
  { value: 'downsell', label: 'Downsell Experience' },
  { value: 'fallback', label: 'Fallback Experience' },
]

export function EditStepModal({ isOpen, onClose, onSave, initialData, stepsCount, allSteps = [] }: EditStepModalProps) {
  const [name, setName] = useState('')
  const [stepType, setStepType] = useState('upsell')
  const [position, setPosition] = useState(stepsCount)
  const [offerId, setOfferId] = useState('')
  const [nextStepAccept, setNextStepAccept] = useState('')
  const [nextStepDecline, setNextStepDecline] = useState('')
  const [experienceType, setExperienceType] = useState('')
  const [useOfferSet, setUseOfferSet] = useState(false)
  const [showOfferEditor, setShowOfferEditor] = useState(false)
  const [offerDraft, setOfferDraft] = useState<OfferEditorValue>({
    name: 'Offer Draft',
    ctaText: 'Yes! Add to my order',
    declineText: 'No thanks',
    pricingType: 'fixed',
    pricingValue: 0,
    shippingType: 'free',
    shippingValue: 0,
    displayQuantity: true,
    limitQuantity: false,
    maxQuantity: 1,
    isActive: true,
  })

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || '')
      setStepType(initialData.stepType || 'upsell')
      setPosition(initialData.position ?? stepsCount)
      setOfferId(initialData.offerId || '')
      setNextStepAccept(initialData.nextStepAccept || '')
      setNextStepDecline(initialData.nextStepDecline || '')
      setExperienceType(initialData.experienceType || '')
      if (initialData.offer) {
        setOfferDraft({
          name: initialData.offer.name || 'Offer Draft',
          description: initialData.offer.description,
          productId: initialData.offer.productId,
          variantId: initialData.offer.variantId,
          headline: initialData.offer.headline,
          imageUrl: initialData.offer.imageUrl,
          ctaText: initialData.offer.ctaText || 'Yes! Add to my order',
          declineText: initialData.offer.declineText || 'No thanks',
          pricingType: initialData.offer.pricingType || 'fixed',
          pricingValue: initialData.offer.pricingValue ?? 0,
          originalPrice: initialData.offer.originalPrice,
          shippingType: initialData.offer.shippingType || 'free',
          shippingValue: initialData.offer.shippingValue ?? 0,
          displayQuantity: initialData.offer.displayQuantity ?? true,
          limitQuantity: initialData.offer.limitQuantity ?? false,
          maxQuantity: initialData.offer.maxQuantity ?? 1,
          bannerText: initialData.offer.bannerText,
          subscriptionType: initialData.offer.subscriptionType ?? 'none',
          subscriptionName: initialData.offer.subscriptionName,
          parentOfferId: initialData.offer.parentOfferId,
          minStock: initialData.offer.minStock,
          fallbackProductId: initialData.offer.fallbackProductId,
          isActive: initialData.offer.isActive,
        })
      }
      setUseOfferSet(false)
      setShowOfferEditor(false)
    } else {
      setName('')
      setStepType('upsell')
      setPosition(stepsCount)
      setOfferId('')
      setNextStepAccept('')
      setNextStepDecline('')
      setExperienceType('')
      setUseOfferSet(false)
      setShowOfferEditor(false)
    }
    setSaving(false)
    setSaveError(null)
  }, [initialData, stepsCount, isOpen])

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!name.trim() || saving) return

    setSaving(true)
    setSaveError(null)

    try {
      const nextOfferDraft = !useOfferSet && showOfferEditor ? offerDraft : undefined
      await onSave({
        name: name.trim(),
        stepType,
        position,
        offerId: offerId || undefined,
        nextStepAccept: nextStepAccept || undefined,
        nextStepDecline: nextStepDecline || undefined,
        experienceType: experienceType || undefined,
      }, nextOfferDraft)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save step.'
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const otherSteps = allSteps.filter((s) => s.id !== initialData?.id)

  return (
    <DialogPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-fn-card rounded-lg shadow-fn-modal border border-fn-border animate-scale-in max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-fn-border">
          <h2 className="text-lg font-semibold text-fn-text">
            {initialData ? 'Edit Step' : 'Create New Step'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-fn-hover transition-colors">
            <X className="w-5 h-5 text-fn-text-muted" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          {saveError && (
            <div className="px-3 py-2 text-sm text-red-600 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-md">
              {saveError}
            </div>
          )}
          <div>
            <label className={labelClass}>
              Step Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={inputClass}
              placeholder="Enter step name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className={labelClass}>Step Type</label>
            <div className="relative">
              <select
                className={selectClass}
                value={stepType}
                onChange={(e) => setStepType(e.target.value)}
              >
                {STEP_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
            </div>
          </div>

          <div>
            <label className={labelClass}>Position</label>
            <input
              type="number"
              className={inputClass}
              placeholder="Enter position"
              value={String(position)}
              onChange={(e) => setPosition(parseInt(e.target.value, 10) || 0)}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className={`text-sm ${!useOfferSet ? 'font-semibold text-fn-text' : 'text-fn-text-muted'}`}>
                Single Offer
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useOfferSet}
                  onChange={(e) => setUseOfferSet(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-8 h-[18px] bg-tf-bg-hover peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-[14px] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-tf-bg-card after:border-tf-border after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-primary" />
              </label>
              <span className={`text-sm ${useOfferSet ? 'font-semibold text-fn-text' : 'text-fn-text-muted'}`}>
                A/B Test (Offer Set)
              </span>
            </div>

            {useOfferSet ? (
              <OfferSetEditor
                offerSetId={offerId || undefined}
                onSelect={(setId) => setOfferId(setId)}
                onCancel={() => setUseOfferSet(false)}
              />
            ) : (
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Offer ID (optional)</label>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="Enter offer ID"
                    value={offerId}
                    onChange={(e) => setOfferId(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="text-xs text-primary hover:text-primary/80 font-medium"
                  onClick={() => setShowOfferEditor((prev) => !prev)}
                >
                  {showOfferEditor ? 'Hide Offer Editor' : 'Open Offer Editor'}
                </button>
                {showOfferEditor && (
                  <OfferEditor value={offerDraft} onChange={setOfferDraft} />
                )}
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Next Step (Accept)</label>
            <div className="relative">
              <select
                className={selectClass}
                value={nextStepAccept}
                onChange={(e) => setNextStepAccept(e.target.value)}
              >
                <option value="">No next step</option>
                {otherSteps.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || `Step ${s.position}`} ({s.stepType})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
            </div>
          </div>

          <div>
            <label className={labelClass}>Next Step (Decline)</label>
            <div className="relative">
              <select
                className={selectClass}
                value={nextStepDecline}
                onChange={(e) => setNextStepDecline(e.target.value)}
              >
                <option value="">No next step</option>
                {otherSteps.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || `Step ${s.position}`} ({s.stepType})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
            </div>
          </div>

          <div>
            <label className={labelClass}>Experience Type (optional)</label>
            <div className="relative">
              <select
                className={selectClass}
                value={experienceType}
                onChange={(e) => setExperienceType(e.target.value)}
              >
                <option value="">None</option>
                {EXPERIENCE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-fn-border">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : initialData ? 'Save Changes' : 'Create Step'}
          </Button>
        </div>
      </div>
    </div>
    </DialogPortal>
  )
}
